import { type RefObject, useId } from 'react';
import { Alert, Badge, Button } from '@/design-system';
import type { Catalogue, DocumentRequirement } from '@/lib/intake/catalogue';
import { ACCEPT, type AttachedFile, kilobytes } from '@/lib/intake/documents';
import type { Documents } from '@/lib/intake/useDocuments';
import { COPY } from './copy';
import styles from './Journey.module.css';
import own from './DocumentsStep.module.css';

export interface DocumentsStepProps {
  headingRef: RefObject<HTMLHeadingElement | null>;
  catalogue: Catalogue;
  highlighted: readonly string[];
  documents: Documents;
  /** Files may be added: a draft, or one reopened for more information. */
  canAttach: boolean;
  /** Files may be removed: a draft only. */
  canRemove: boolean;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Step 9: one card per document the central banks expect, each with its files. A
 * file goes straight to storage from here, then waits for its malware scan; the list
 * says which state each is in.
 */
export function DocumentsStep({ headingRef, ...props }: DocumentsStepProps) {
  return (
    <section className={styles.card} aria-labelledby="step-heading">
      <h1 id="step-heading" ref={headingRef} tabIndex={-1} className={styles.title}>
        {COPY.documentsTitle}
      </h1>
      <p className={styles.intro}>
        {COPY.documentsIntro} {COPY.documentsLater}
      </p>
      <p className={styles.hint}>{COPY.documentsLimits}</p>
      {props.documents.loadFailed && <Alert tone="danger">{COPY.documentsUnavailable}</Alert>}
      <ul className={own.requirements}>
        {props.catalogue.documents.map((requirement) => (
          <RequirementCard
            key={requirement.id}
            requirement={requirement}
            highlighted={props.highlighted.includes(requirement.id)}
            documents={props.documents}
            canAttach={props.canAttach}
            canRemove={props.canRemove}
          />
        ))}
      </ul>
      <div className={styles.actions}>
        <Button variant="secondary" onClick={props.onBack}>
          {COPY.back}
        </Button>
        <Button onClick={props.onNext}>{COPY.toReview}</Button>
      </div>
    </section>
  );
}

function RequirementCard({
  requirement,
  highlighted,
  documents,
  canAttach,
  canRemove,
}: {
  requirement: DocumentRequirement;
  highlighted: boolean;
  documents: Documents;
  canAttach: boolean;
  canRemove: boolean;
}) {
  const inputId = useId();
  const titleId = `${inputId}-title`;
  const files = (documents.files ?? []).filter((f) => f.requirementId === requirement.id);
  const uploads = documents.uploads.filter((u) => u.requirementId === requirement.id);
  const problems = documents.problems.filter((p) => p.requirementId === requirement.id);
  return (
    <li className={[own.requirement, highlighted ? own.highlighted : ''].join(' ')} aria-labelledby={titleId}>
      <div className={own.requirementHeader}>
        <h2 id={titleId} className={own.requirementTitle}>
          {requirement.title}
        </h2>
        {highlighted && <Badge tone="accent">{COPY.documentsRelevant}</Badge>}
      </div>
      {(files.length > 0 || uploads.length > 0) && (
        <ul className={own.files}>
          {files.map((file) => (
            <FileRow key={file.fileId} file={file} canRemove={canRemove} onRemove={() => void documents.remove(file.fileId)} />
          ))}
          {uploads.map((upload) => {
            const percent = Math.round(upload.progress * 100);
            return (
              <li key={upload.id} className={own.file}>
                <span className={own.fileName}>{upload.fileName}</span>
                <progress className={own.progress} max={100} value={percent} aria-label={COPY.uploading(percent)} />
              </li>
            );
          })}
        </ul>
      )}
      {problems.map((problem) => (
        <Alert key={`${problem.fileName}-${problem.message}`} tone="danger" title={`${COPY.notAttached}: ${problem.fileName}`}>
          {problem.message}
        </Alert>
      ))}
      {canAttach && (
        <div>
          {/* A real file input, visually replaced: keyboard and screen readers reach it as itself. */}
          <input
            id={inputId}
            type="file"
            multiple
            accept={ACCEPT}
            className={own.fileInput}
            aria-label={COPY.attachTo(requirement.title)}
            onChange={(event) => {
              const chosen = Array.from(event.target.files ?? []);
              event.target.value = '';
              if (chosen.length > 0) void documents.attach(requirement.id, chosen);
            }}
          />
          <label htmlFor={inputId} className={own.attach} aria-hidden="true">
            {COPY.attach}
          </label>
        </div>
      )}
    </li>
  );
}

function FileRow({ file, canRemove, onRemove }: { file: AttachedFile; canRemove: boolean; onRemove: () => void }) {
  const tone = file.scanStatus === 'CLEAN' ? 'success' : file.scanStatus === 'PENDING' ? 'neutral' : 'danger';
  return (
    <li className={own.file}>
      <span className={own.fileName}>{file.fileName}</span>
      <span className={own.fileSize}>{kilobytes(file.sizeBytes)}</span>
      {file.scanStatus === 'CLEAN' || file.scanStatus === 'PENDING' ? (
        <Badge tone={tone}>{COPY.scan[file.scanStatus]}</Badge>
      ) : (
        <span className={own.fileProblem}>{COPY.scan[file.scanStatus]}</span>
      )}
      {canRemove && (
        <Button variant="ghost" size="sm" onClick={onRemove} aria-label={COPY.removeFile(file.fileName)}>
          {COPY.remove}
        </Button>
      )}
    </li>
  );
}
